import { useEffect, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import refreshAccessToken from '@core/actions/accessToken/refreshAccessToken.action';
import {
  fetchHomeUpcomingAssignments,
  HomeUpcomingAssignmentCount,
} from '@core/assignments/api/homeUpcomingAssignments';
import { getInitialSpaceId } from '@core/selectors/space/space.selectors';
import {
  getHomeViewRole,
  getUpcomingAssignmentDueSoonCount,
} from '@app/selectors/home';
import setUpcomingAssignmentCounts from '@app/actions/home/setUpcomingAssignmentCounts';

const countsFromAssignments = (
  assignments: Array<{ status?: string }> = []
): HomeUpcomingAssignmentCount[] => {
  const counts = { dueSoon: 0, upcoming: 0, open: 0 };

  assignments.forEach((assignment) => {
    if (assignment.status === 'DueSoon') counts.dueSoon += 1;
    if (assignment.status === 'Upcoming') counts.upcoming += 1;
    if (assignment.status === 'Open') counts.open += 1;
  });

  return [
    { assignmentStatus: 'dueSoon', count: counts.dueSoon },
    { assignmentStatus: 'upcoming', count: counts.upcoming },
    { assignmentStatus: 'open', count: counts.open },
  ];
};

const getDueSoonCount = (
  assignmentCounts?: HomeUpcomingAssignmentCount[] | null
) =>
  assignmentCounts?.find((item) => item.assignmentStatus === 'dueSoon')
    ?.count ?? 0;

/**
 * Keeps Due Soon count available for Manage widgets (same idea as unread
 * messages), including when the Upcoming assignments widget is hidden.
 */
export const useUpcomingAssignmentDueSoonCount = () => {
  const dispatch = useDispatch<any>();
  const store = useStore<any>();
  const viewRole = useSelector(getHomeViewRole);
  // Same token space as the upcoming-assignments list hook.
  const spaceId = useSelector((state: any) => getInitialSpaceId(state));
  const countFromStore = useSelector(getUpcomingAssignmentDueSoonCount);
  const [count, setCount] = useState(countFromStore);

  useEffect(() => {
    setCount(countFromStore);
  }, [countFromStore]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!spaceId || (typeof navigator !== 'undefined' && !navigator.onLine)) {
        return;
      }

      try {
        const accessToken = await refreshAccessToken(
          dispatch,
          store.getState
        )({ spaceId });

        const { data } = await fetchHomeUpcomingAssignments({
          accessToken,
          limit: 1,
          page: 0,
          includeSubmitted: true,
          role: viewRole,
          count: true,
        });

        // Same shape as useHomeUpcomingAssignments (flat axios body).
        const payload = (data as any)?.result ?? data;
        const assignmentCounts =
          payload?.assignmentCounts ??
          countsFromAssignments(payload?.upcomingAssignments);

        const dueSoonCount = getDueSoonCount(assignmentCounts);

        if (cancelled) {
          return;
        }

        setCount(dueSoonCount);
        dispatch(setUpcomingAssignmentCounts(assignmentCounts));
      } catch {
        // Keep last known count.
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [dispatch, spaceId, store, viewRole]);

  return count;
};

export default useUpcomingAssignmentDueSoonCount;
